import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, Package, Music4, CalendarHeart, CalendarDays, CalendarRange, BarChart3, ScanLine, LogOut, ArrowLeft, TicketPercent, Receipt, QrCode, Palette, ClipboardList, Archive, Wine, ShoppingBag } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "admin-nav-dashboard" },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays, tid: "admin-nav-calendar" },
  { to: "/admin/terms", label: "Terms", icon: CalendarRange, tid: "admin-nav-terms" },
  { to: "/admin/members", label: "Members", icon: Users, tid: "admin-nav-members" },
  { to: "/admin/catalog", label: "Packages / Codes", icon: Package, tid: "admin-nav-catalog" },
  { to: "/admin/scanner", label: "Scanner", icon: ScanLine, tid: "admin-nav-scanner" },
  { to: "/admin/social-roster", label: "Social Roster", icon: ClipboardList, tid: "admin-nav-social-roster" },
  { to: "/admin/payments", label: "Payments", icon: Receipt, tid: "admin-nav-payments" },
  { to: "/admin/purchases", label: "Purchases", icon: ShoppingBag, tid: "admin-nav-purchases" },
  { to: "/admin/payment-methods", label: "Pay Methods", icon: QrCode, tid: "admin-nav-payment-methods" },
  { to: "/admin/design", label: "Design", icon: Palette, tid: "admin-nav-design" },
  { to: "/admin/redemptions", label: "Redemptions", icon: TicketPercent, tid: "admin-nav-redemptions" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, tid: "admin-nav-reports" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchCount = () => api.get("/admin/orders/pending-count").then((r) => { if (alive) setPendingCount(r.data.count || 0); }).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [pathname]);

  const scannerOnly = ["instructor", "staff"].includes(user?.role);
  const SCANNER_ONLY_PATHS = ["/admin/scanner", "/admin/drinks"];
  const links = (scannerOnly
    ? LINKS.filter((l) => SCANNER_ONLY_PATHS.includes(l.to))
    : LINKS.filter((l) => l.to !== "/admin/design" || user?.role === "super_admin" || user?.can_edit_design)
  ).sort((a, b) => {
    if (a.to === "/admin/scanner") return -1;
    if (b.to === "/admin/scanner") return 1;
    return a.label.localeCompare(b.label);
  });
  if (scannerOnly && !SCANNER_ONLY_PATHS.includes(pathname)) return <Navigate to="/admin/scanner" replace />;

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-[#D4AF37] text-black" : "text-[#A19DB0] hover:bg-white/5 hover:text-white"}`;

  return (
    <div className="min-h-[100dvh] w-full bg-[#0D0B14] text-white flex flex-col lg:flex-row">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/10 p-5 sticky top-0 h-[100dvh]">
        <div className="mb-8">
          <p className="text-[#D4AF37] font-display text-3xl font-bold tracking-wide">DARKCHATA</p>
          <p className="text-[#A19DB0] text-[11px] uppercase tracking-[0.4em] mt-1">Admin</p>
        </div>
        <nav className="flex-1 space-y-1.5">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.tid} className={linkClass}>
              <l.icon size={18} /> <span className="flex-1">{l.label}</span>
              {l.to === "/admin/payments" && pendingCount > 0 && <span data-testid="payments-pending-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#E57373] text-white text-[11px] font-bold flex items-center justify-center">{pendingCount}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="pt-4 border-t border-white/10 space-y-1">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center text-sm font-bold shrink-0">{(user?.name || "AD").slice(0, 2).toUpperCase()}</div>
            <div className="min-w-0"><p className="text-sm font-medium truncate">{user?.name || "Admin"}</p><p className="text-[#A19DB0] text-xs truncate">{user?.email}</p></div>
          </div>
          <button onClick={() => navigate("/")} data-testid="admin-back-to-app" className="w-full flex items-center gap-3 px-4 py-2 rounded-xl text-sm text-[#A19DB0] hover:bg-white/5">
            <ArrowLeft size={18} /> Member app
          </button>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#E57373] hover:bg-white/5">
            <LogOut size={18} /> Log out
          </button>
          <p className="text-[#A19DB0]/50 text-[10px] px-4 pt-3 tracking-[0.3em] uppercase">Dance • People • Beyond</p>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="lg:hidden sticky top-0 z-30 bg-[#0D0B14]/90 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="font-display text-xl font-bold">Darkchata Admin</h1>
          <button onClick={() => navigate("/")} data-testid="admin-back-to-app-m" className="text-[#A19DB0]"><ArrowLeft size={20} /></button>
        </div>
        <nav className="flex gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`${l.tid}-m`}
              className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-sm border flex items-center gap-1.5 ${isActive ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 text-[#A19DB0] border-white/10"}`}>
              {l.label}
              {l.to === "/admin/payments" && pendingCount > 0 && <span data-testid="payments-pending-badge-m" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#E57373] text-white text-[10px] font-bold flex items-center justify-center">{pendingCount}</span>}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 min-w-0 p-5 lg:p-8 max-w-6xl mx-auto w-full">
        <Outlet />
      </main>
    </div>
  );
}

export const AdminHeader = ({ title, subtitle, action }) => (
  <div className="flex items-end justify-between mb-6 gap-4">
    <div>
      <h2 className="font-display text-3xl font-bold">{title}</h2>
      {subtitle && <p className="text-[#A19DB0] text-sm mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const AdminModal = ({ open, onOpenChange, title, children, onSubmit, submitLabel = "Save", busy }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#140F1E] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
      <DialogDescription className="sr-only">{title}</DialogDescription>
      <div className="space-y-4 py-1">{children}</div>
      <DialogFooter className="gap-2">
        <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm">Cancel</button>
        <button onClick={onSubmit} disabled={busy} data-testid="admin-modal-submit" className="px-5 py-2 rounded-full bg-[#D4AF37] text-black font-semibold text-sm disabled:opacity-60">{busy ? "Saving…" : submitLabel}</button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AField = ({ label, ...props }) => (
  <div>
    <label className="text-xs text-[#A19DB0]">{label}</label>
    <input {...props} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 outline-none focus:border-[#D4AF37] text-sm transition-colors" />
  </div>
);

export const ATextarea = ({ label, ...props }) => (
  <div>
    <label className="text-xs text-[#A19DB0]">{label}</label>
    <textarea {...props} rows={3} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 outline-none focus:border-[#D4AF37] text-sm resize-none transition-colors" />
  </div>
);

export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
export const fromLocalInput = (val) => (val ? new Date(val).toISOString() : null);

export const SWATCHES = ["#9D4EDD", "#D4AF37", "#5CC8FF", "#E57373", "#4ADE80", "#F59E0B"];
export const ColorField = ({ value, onChange }) => (
  <div>
    <label className="text-xs text-[#A19DB0]">Colour</label>
    <div className="flex gap-2 mt-1">
      {SWATCHES.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)} data-testid={`swatch-${c}`}
          className={`w-7 h-7 rounded-full border-2 transition-transform active:scale-90 ${value === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />
      ))}
    </div>
  </div>
);
